import { RefObject, useCallback, useEffect, useState } from 'react';

export interface NodePosition {
  left: number;
  center: number;
  width: number;
}

export interface ProgressLinePositions {
  backgroundLine: {
    left: number;
    width: number;
  };
  completedLine: {
    left: number;
    width: number;
  };
  currentLine: {
    left: number;
    width: number;
  };
}

/**
 * Custom hook to calculate actual DOM positions of step nodes
 * and derive progress line positions from them
 */
export const useNodePositions = (
  containerRef: RefObject<HTMLElement | null>,
  stepCount: number,
  activeStepIndex: number,
  progressPercentage: number,
) => {
  const [nodePositions, setNodePositions] = useState<NodePosition[]>([]);
  const [containerWidth, setContainerWidth] = useState(0);

  // Calculate node positions from actual DOM
  const calculatePositions = useCallback(() => {
    if (!containerRef.current || stepCount === 0) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const nodes = container.querySelectorAll('[data-step-node]');

    if (nodes.length !== stepCount) return;

    const positions: NodePosition[] = [];
    const totalWidth = containerRect.width;

    nodes.forEach((node) => {
      const nodeRect = node.getBoundingClientRect();
      const left = nodeRect.left - containerRect.left;
      const center = left + nodeRect.width / 2;
      const width = nodeRect.width;

      positions.push({ left, center, width });
    });

    setNodePositions(positions);
    setContainerWidth(totalWidth);
  }, [containerRef, stepCount]);

  // Recalculate on mount, step count change, window resize, and container resize
  useEffect(() => {
    if (!containerRef.current) return;

    // Initial calculation
    const timeoutId = setTimeout(() => {
      calculatePositions();
    }, 0);

    // Use ResizeObserver to detect when StepsContainer changes size
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize calculations
      const debounceTimeout = setTimeout(calculatePositions, 50);
      return () => clearTimeout(debounceTimeout);
    });

    resizeObserver.observe(containerRef.current);

    const handleResize = () => {
      // Debounce resize calculations
      const debounceTimeout = setTimeout(calculatePositions, 100);
      return () => clearTimeout(debounceTimeout);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calculatePositions, stepCount]);

  // Calculate progress line positions based on actual node positions
  const progressLinePositions: ProgressLinePositions | null =
    nodePositions.length > 0 && nodePositions.length === stepCount
      ? {
          // Background line: from center of first node to center of last node
          // This ensures it spans exactly between step centers, not beyond
          backgroundLine: {
            left: nodePositions[0].center,
            width:
              nodePositions[nodePositions.length - 1].center -
              nodePositions[0].center,
          },
          // Completed line: from center of first node to center of active node
          completedLine: {
            left: nodePositions[0].center,
            width:
              activeStepIndex >= 0
                ? nodePositions[activeStepIndex].center -
                  nodePositions[0].center
                : 0,
          },
          // Current progress line: extends through active step and towards next step
          // For first step: starts from left edge, extends based on progress
          // For other steps: starts from center (where completed line ends), extends based on progress
          currentLine: {
            left:
              activeStepIndex >= 0 && activeStepIndex < nodePositions.length
                ? activeStepIndex === 0
                  ? nodePositions[0].left // First step: start from left edge
                  : nodePositions[activeStepIndex].center // Other steps: start from center
                : nodePositions[0].left,
            width:
              activeStepIndex >= 0 && activeStepIndex < nodePositions.length
                ? activeStepIndex < nodePositions.length - 1
                  ? // Has next step: extend based on progress percentage
                    activeStepIndex === 0
                    ? // First step: from left edge to progress point
                      (progressPercentage / 100) *
                      (nodePositions[activeStepIndex + 1].center -
                        nodePositions[activeStepIndex].left)
                    : // Other steps: from center to progress point
                      (progressPercentage / 100) *
                      (nodePositions[activeStepIndex + 1].center -
                        nodePositions[activeStepIndex].center)
                  : // Last step: extend to right edge
                    activeStepIndex === 0
                    ? nodePositions[0].left +
                      nodePositions[0].width -
                      nodePositions[0].left
                    : nodePositions[activeStepIndex].left +
                      nodePositions[activeStepIndex].width -
                      nodePositions[activeStepIndex].center
                : 0,
          },
        }
      : null;

  return {
    nodePositions,
    containerWidth,
    progressLinePositions,
    recalculate: calculatePositions,
  };
};
